import { authMarketingPanelBg } from '@/theme/designTokens'
import {
  LOGIN_ILLUSTRATION_ART_WIDTH_RATIO,
  LOGIN_ILLUSTRATION_HEIGHT_PX,
  LOGIN_ILLUSTRATION_WRAP_WIDTH_CQW,
  LOGIN_ILLUSTRATION_WRAPPER_HEIGHT_PX,
  LOGIN_ILLUSTRATION_WRAPPER_WIDTH_PX,
  LOGIN_ILLUSTRATION_WIDTH_PX,
  LOGIN_MARKETING_CONTENT_MAX_WIDTH_PX,
  LOGIN_MARKETING_HEADLINE_LINE_HEIGHT_PX,
  LOGIN_MARKETING_HEADLINE_SUBTITLE_GAP_PX,
  LOGIN_MARKETING_ILLUSTRATION_TO_COPY_GAP_PX,
  LOGIN_MARKETING_PADDING_BOTTOM_RATIO,
  LOGIN_MARKETING_PADDING_LEFT_RATIO,
  LOGIN_MARKETING_PADDING_RIGHT_RATIO,
  LOGIN_MARKETING_PADDING_TOP_RATIO,
  LOGIN_MARKETING_PANEL_BASIS,
  LOGIN_MARKETING_SIGNALS_TO_STRATEGIC_GAP_PX,
  LOGIN_MARKETING_SUBTITLE_MAX_WIDTH_PX,
  LOGIN_MARKETING_TURN_TO_SIGNALS_GAP_PX,
} from './loginLayoutConstants'

const ILLUSTRATION_ART_WIDTH_PERCENT = LOGIN_ILLUSTRATION_ART_WIDTH_RATIO * 100

/** Fluid gap between min and max using content-column container width (480px ref). */
export function marketingGapPx(minPx: number, maxPx: number) {
  const midCqw = ((maxPx - minPx) / LOGIN_MARKETING_CONTENT_MAX_WIDTH_PX) * 100
  return `clamp(${minPx}px, ${midCqw}cqw, ${maxPx}px)`
}

export const LOGIN_MARKETING_ILLUSTRATION_TO_COPY_GAP = marketingGapPx(
  12,
  LOGIN_MARKETING_ILLUSTRATION_TO_COPY_GAP_PX
)
export const LOGIN_MARKETING_HEADLINE_SUBTITLE_GAP = marketingGapPx(
  12,
  LOGIN_MARKETING_HEADLINE_SUBTITLE_GAP_PX
)
export const LOGIN_MARKETING_TURN_TO_SIGNALS_GAP = marketingGapPx(
  4,
  LOGIN_MARKETING_TURN_TO_SIGNALS_GAP_PX
)

/** Short viewports: shrink illustration via cqw (no transform — transform + overflow:hidden was clipping art). */
const marketingIllustrationHeightFit = {
  '@container (max-height: 860px)': {
    '& [data-testid="login-marketing-illustration-wrap"]': {
      width: `min(${LOGIN_ILLUSTRATION_WRAP_WIDTH_CQW * 0.94}cqw, ${Math.round(LOGIN_ILLUSTRATION_WRAPPER_WIDTH_PX * 0.94)}px)`,
    },
  },
  '@container (max-height: 760px)': {
    '& [data-testid="login-marketing-illustration-wrap"]': {
      width: `min(${LOGIN_ILLUSTRATION_WRAP_WIDTH_CQW * 0.86}cqw, ${Math.round(LOGIN_ILLUSTRATION_WRAPPER_WIDTH_PX * 0.86)}px)`,
    },
  },
  '@container (max-height: 660px)': {
    '& [data-testid="login-marketing-illustration-wrap"]': {
      width: `min(${LOGIN_ILLUSTRATION_WRAP_WIDTH_CQW * 0.78}cqw, ${Math.round(LOGIN_ILLUSTRATION_WRAPPER_WIDTH_PX * 0.78)}px)`,
    },
  },
} as const

/**
 * Figma 5074-11767 — 50% column at md+.
 * Hidden below md: stacking it on phones collapsed to a clipped “Turn” strip
 * (size container + flex:1 / minHeight:0 with height:auto).
 */
export const loginMarketingPanelSx = {
  display: { xs: 'none', md: 'flex' },
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flex: { xs: '0 0 auto', md: `0 0 ${LOGIN_MARKETING_PANEL_BASIS}` },
  width: { xs: '100%', md: LOGIN_MARKETING_PANEL_BASIS },
  maxWidth: { xs: '100%', md: LOGIN_MARKETING_PANEL_BASIS },
  minWidth: 0,
  minHeight: 0,
  height: { xs: 'auto', md: '100%' },
  alignSelf: 'stretch',
  bgcolor: authMarketingPanelBg,
  pl: {
    xs: 'clamp(16px, 5vw, 48px)',
    md: `${LOGIN_MARKETING_PADDING_LEFT_RATIO * 100}%`,
  },
  pr: {
    xs: 'clamp(16px, 4vw, 40px)',
    md: `${LOGIN_MARKETING_PADDING_RIGHT_RATIO * 100}%`,
  },
  pt: {
    xs: 'clamp(16px, 3dvh, 32px)',
    md: `${LOGIN_MARKETING_PADDING_TOP_RATIO * 100}%`,
  },
  pb: {
    xs: 'clamp(16px, 3dvh, 32px)',
    md: `${LOGIN_MARKETING_PADDING_BOTTOM_RATIO * 100}%`,
  },
  overflow: { xs: 'visible', md: 'hidden' },
  boxSizing: 'border-box',
  containerType: 'size',
  ...marketingIllustrationHeightFit,
} as const

/** Fills panel between chrome — overflow visible so layered art (5074-11770) is not clipped. */
export const loginMarketingPanelBodySx = {
  flex: 1,
  minHeight: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  overflow: 'visible',
  boxSizing: 'border-box',
} as const

/** Figma 5074-11768 — section gaps scale with column width. */
export const loginMarketingContentSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: LOGIN_MARKETING_ILLUSTRATION_TO_COPY_GAP,
  width: '100%',
  maxWidth: { xs: '100%', sm: LOGIN_MARKETING_CONTENT_MAX_WIDTH_PX },
  flex: '0 0 auto',
  minHeight: 0,
  boxSizing: 'border-box',
  containerType: 'inline-size',
} as const

/**
 * Figma 5074-11769 — 350×372.97px at 480px content column; scales up/down with cqw (not cqh).
 * @see https://www.figma.com/design/6Dr223bNy8pgoiJmc1Ahw3?node-id=5074-11770
 */
export const loginMarketingIllustrationWrapSx = {
  position: 'relative',
  width: `min(${LOGIN_ILLUSTRATION_WRAP_WIDTH_CQW}cqw, ${LOGIN_ILLUSTRATION_WRAPPER_WIDTH_PX}px)`,
  maxWidth: LOGIN_ILLUSTRATION_WRAPPER_WIDTH_PX,
  aspectRatio: `${LOGIN_ILLUSTRATION_WRAPPER_WIDTH_PX} / ${LOGIN_ILLUSTRATION_WRAPPER_HEIGHT_PX}`,
  flexShrink: 0,
  minWidth: 0,
  overflow: 'visible',
  boxSizing: 'border-box',
} as const

/** Figma 5074-11770 — 333×347 at top-left of 5074-11769 (aspect-ratio, not fixed % height). */
export const loginMarketingIllustrationPositionSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: `${ILLUSTRATION_ART_WIDTH_PERCENT}%`,
  aspectRatio: `${LOGIN_ILLUSTRATION_WIDTH_PX} / ${LOGIN_ILLUSTRATION_HEIGHT_PX}`,
  height: 'auto',
  overflow: 'visible',
  boxSizing: 'border-box',
} as const

/** Figma 5074-11913 — 5074-11914 + 5074-11922 */
export const loginMarketingCopyBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: LOGIN_MARKETING_HEADLINE_SUBTITLE_GAP,
  width: '100%',
  minWidth: 0,
  flexShrink: 0,
} as const

/** Figma 5074-11914 — 5074-11916 + 5074-11917 */
export const loginMarketingHeadlineBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: LOGIN_MARKETING_TURN_TO_SIGNALS_GAP,
  width: '100%',
} as const

const headlineFontSize = `clamp(28px, ${(LOGIN_MARKETING_HEADLINE_LINE_HEIGHT_PX / LOGIN_MARKETING_CONTENT_MAX_WIDTH_PX) * 100}cqw, 72px)`

/** Figma 5074-11916 / 5074-11918 / 5074-11919 — unitless line-height so copy fits short viewports */
export const loginMarketingHeadlineLineSx = {
  m: 0,
  p: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: headlineFontSize,
  lineHeight: 1.05,
  letterSpacing: 'clamp(-1.1px, -0.57cqw, -2.757px)',
  color: '#ffffff',
} as const

/** Figma 5074-11917 */
export const loginMarketingHeadlineSecondBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: marketingGapPx(4, LOGIN_MARKETING_SIGNALS_TO_STRATEGIC_GAP_PX),
  width: '100%',
} as const

/** Figma 5074-11919 — Strategic / Decisions two-line block */
export const loginMarketingHeadlineStrategicSx = {
  ...loginMarketingHeadlineLineSx,
  whiteSpace: 'pre-line',
} as const

/** Figma 5074-11922 */
export const loginMarketingSubtitleSx = {
  m: 0,
  p: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: 'clamp(13px, 3.33cqw, 16px)',
  lineHeight: 1.625,
  letterSpacing: '-0.3125px',
  color: 'rgba(255,255,255,0.88)',
  maxWidth: `${(LOGIN_MARKETING_SUBTITLE_MAX_WIDTH_PX / LOGIN_MARKETING_CONTENT_MAX_WIDTH_PX) * 100}%`,
  width: '100%',
  boxSizing: 'border-box',
  flexShrink: 0,
} as const
